import {
  FilterModeSchema,
  type FilterRelative,
  type FilterType,
  type RelativeIntervalType,
} from "../types";
import { escapeIdentifier } from "./escape-identifier";
import { escapeLiteral } from "./escape-literal";

const DATE_TRUNC_UNITS: Record<RelativeIntervalType, string> = {
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  QUARTER: "quarter",
  YEAR: "year",
};

/**
 * Options for resolving a relative date window.
 *
 * When `now` is provided, windows are computed in JS against that reference
 * (browser-local time on the client) and emitted as literal timestamps, so the
 * query matches exactly what the user sees. When omitted, the SQL falls back to
 * DuckDB's `CURRENT_TIMESTAMP` (the engine's timezone), preserving prior
 * server-side behavior.
 */
export type RelativeWindowOptions = { now?: Date };

/** Returns a DuckDB interval expression for a relative window size. */
export function getIntervalSql(
  amount: number,
  interval: RelativeIntervalType,
): string {
  switch (interval) {
    case "QUARTER":
      return `INTERVAL ${amount * 3} MONTH`;
    default:
      return `INTERVAL ${amount} ${interval}`;
  }
}

/** Truncates a date to the start of its period (local time). Week starts Sunday. */
function getPeriodStart(date: Date, interval: RelativeIntervalType): Date {
  const y = date.getFullYear();
  const mo = date.getMonth();
  const day = date.getDate();
  switch (interval) {
    case "HOUR":
      return new Date(y, mo, day, date.getHours());
    case "DAY":
      return new Date(y, mo, day);
    case "WEEK": {
      const start = new Date(y, mo, day);
      start.setDate(start.getDate() - start.getDay());
      return start;
    }
    case "MONTH":
      return new Date(y, mo, 1);
    case "QUARTER":
      return new Date(y, Math.floor(mo / 3) * 3, 1);
    case "YEAR":
      return new Date(y, 0, 1);
    default:
      return new Date(y, mo, day);
  }
}

/** Adds `n` whole periods of `interval` to a date (local time). `n` may be negative. */
export function addRelativeInterval(
  date: Date,
  n: number,
  interval: RelativeIntervalType,
): Date {
  const next = new Date(date);
  switch (interval) {
    case "HOUR":
      next.setHours(next.getHours() + n);
      break;
    case "DAY":
      next.setDate(next.getDate() + n);
      break;
    case "WEEK":
      next.setDate(next.getDate() + n * 7);
      break;
    case "MONTH":
      next.setMonth(next.getMonth() + n);
      break;
    case "QUARTER":
      next.setMonth(next.getMonth() + n * 3);
      break;
    case "YEAR":
      next.setFullYear(next.getFullYear() + n);
      break;
  }
  return next;
}

/**
 * Computes the concrete inclusive-exclusive `[start, end)` window for a relative
 * date filter against a reference `now`, using local-time calendar math.
 *
 * This is the single source of truth shared by the SQL generator and the
 * display label, so the rows queried always match the calendar the user sees.
 */
export function getRelativeWindowBounds(
  relative: FilterRelative,
  now: Date,
): { start: Date; end: Date } | null {
  const amount = Math.max(1, Math.floor(relative.value));
  if (!Number.isFinite(amount)) return null;

  const periodStart = getPeriodStart(now, relative.interval);

  switch (relative.mode) {
    case "PREVIOUS":
      return {
        start: addRelativeInterval(periodStart, -amount, relative.interval),
        end: periodStart,
      };
    case "CURRENT":
      return {
        start: periodStart,
        end: addRelativeInterval(periodStart, amount, relative.interval),
      };
    case "NEXT": {
      const nextStart = addRelativeInterval(periodStart, 1, relative.interval);
      return {
        start: nextStart,
        end: addRelativeInterval(nextStart, amount, relative.interval),
      };
    }
    default:
      return null;
  }
}

/** Formats a date as a DuckDB naive timestamp literal in local wall-clock time. */
export function toTimestampLiteral(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const mo = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const h = pad(date.getHours());
  const m = pad(date.getMinutes());
  const s = pad(date.getSeconds());
  return `TIMESTAMP '${y}-${mo}-${day} ${h}:${m}:${s}'`;
}

/** Builds an inclusive-exclusive timestamp window for a relative date filter. */
export function getRelativeWindowSql(
  relative: FilterRelative,
  opts?: RelativeWindowOptions,
): {
  lowerBound: string;
  upperBound: string;
} | null {
  const amount = Math.max(1, Math.floor(relative.value));
  if (!Number.isFinite(amount)) return null;

  // Local-anchored window: resolve concrete bounds in JS and emit literals so
  // the query matches what the user sees in the calendar/pill.
  if (opts?.now) {
    const bounds = getRelativeWindowBounds(relative, opts.now);
    if (!bounds) return null;
    return {
      lowerBound: toTimestampLiteral(bounds.start),
      upperBound: toTimestampLiteral(bounds.end),
    };
  }

  const currentPeriodStart = `DATE_TRUNC('${DATE_TRUNC_UNITS[relative.interval]}', CURRENT_TIMESTAMP::TIMESTAMP)`;
  const intervalSql = getIntervalSql(amount, relative.interval);

  switch (relative.mode) {
    case "PREVIOUS":
      return {
        lowerBound: `${currentPeriodStart} - ${intervalSql}`,
        upperBound: currentPeriodStart,
      };
    case "CURRENT":
      return {
        lowerBound: currentPeriodStart,
        upperBound: `${currentPeriodStart} + ${intervalSql}`,
      };
    case "NEXT": {
      const nextPeriodStart = `${currentPeriodStart} + ${getIntervalSql(1, relative.interval)}`;
      return {
        lowerBound: nextPeriodStart,
        upperBound: `${nextPeriodStart} + ${intervalSql}`,
      };
    }
    default:
      return null;
  }
}

/** Generates SQL for a relative date filter window. */
function withinColumnFilterSql(
  column: string,
  relative: FilterRelative | null,
  opts?: RelativeWindowOptions,
): string {
  if (!relative) return "1=1";

  const window = getRelativeWindowSql(relative, opts);
  if (!window) return "1=1";

  const columnTimestamp = `CAST(${escapeIdentifier(column)} AS TIMESTAMP)`;
  return `(${columnTimestamp} >= ${window.lowerBound} AND ${columnTimestamp} < ${window.upperBound})`;
}

/** Generates SQL for a single column with the filter's mode and value. */
function singleColumnFilterSql(
  column: string,
  filter: FilterType,
  opts?: RelativeWindowOptions,
): string {
  const col = escapeIdentifier(column);
  const asVarchar = `CAST(${col} AS VARCHAR)`;
  const lower = `LOWER(${asVarchar})`;

  switch (filter.mode) {
    case FilterModeSchema.enum.EQUAL:
      return `${col} = ${escapeLiteral(filter.value)}`;
    case FilterModeSchema.enum.NOT_EQUAL:
      return `${col} != ${escapeLiteral(filter.value)}`;
    case FilterModeSchema.enum.GREATER:
      return `${col} > ${escapeLiteral(filter.value)}`;
    case FilterModeSchema.enum.GREATER_EQUAL:
      return `${col} >= ${escapeLiteral(filter.value)}`;
    case FilterModeSchema.enum.LESS:
      return `${col} < ${escapeLiteral(filter.value)}`;
    case FilterModeSchema.enum.LESS_EQUAL:
      return `${col} <= ${escapeLiteral(filter.value)}`;
    case FilterModeSchema.enum.CONTAINS:
      return `${lower} LIKE ${escapeLiteral(`%${String(filter.value ?? "").toLowerCase()}%`)}`;
    case FilterModeSchema.enum.NOT_CONTAINS:
      return `${lower} NOT LIKE ${escapeLiteral(`%${String(filter.value ?? "").toLowerCase()}%`)}`;
    case FilterModeSchema.enum.STARTS:
      return `${lower} LIKE ${escapeLiteral(`${String(filter.value ?? "").toLowerCase()}%`)}`;
    case FilterModeSchema.enum.ENDS:
      return `${lower} LIKE ${escapeLiteral(`%${String(filter.value ?? "").toLowerCase()}`)}`;
    case FilterModeSchema.enum.MATCHES:
      return `regexp_matches(${asVarchar}, ${escapeLiteral(String(filter.value ?? ""))})`;
    case FilterModeSchema.enum.EMPTY:
      return `(${col} IS NULL OR ${asVarchar} = '')`;
    case FilterModeSchema.enum.NOT_EMPTY:
      return `(${col} IS NOT NULL AND ${asVarchar} != '')`;
    case FilterModeSchema.enum.IS_TRUE:
      return `${col} = TRUE`;
    case FilterModeSchema.enum.IS_FALSE:
      return `${col} = FALSE`;
    case FilterModeSchema.enum.RELATIVE:
      return withinColumnFilterSql(column, filter.relative, opts);
    case FilterModeSchema.enum.BETWEEN: {
      const range = filter.value as { start?: string; end?: string } | null;
      if (!range?.start || !range?.end) return "1=1";
      const start = String(range.start);
      const end = String(range.end);
      const columnTimestamp = `CAST(${col} AS TIMESTAMP)`;
      return `(${columnTimestamp} >= ${escapeLiteral(start)}::DATE AND ${columnTimestamp} <= ${escapeLiteral(end)}::DATE)`;
    }
    case FilterModeSchema.enum.IN: {
      const values = Array.isArray(filter.value) ? filter.value : [];
      if (values.length === 0) return "1=0";
      const literals = values.map((v: unknown) => escapeLiteral(v));
      return `${asVarchar} IN (${literals.join(", ")})`;
    }
    case FilterModeSchema.enum.NOT_IN: {
      const values = Array.isArray(filter.value) ? filter.value : [];
      if (values.length === 0) return "1=1";
      const literals = values.map((v: unknown) => escapeLiteral(v));
      return `${asVarchar} NOT IN (${literals.join(", ")})`;
    }
    default:
      return "1=1";
  }
}

export const filterToSql = (
  filter: FilterType,
  opts?: RelativeWindowOptions,
): string => {
  const parts = filter.columns.map((col) =>
    singleColumnFilterSql(col, filter, opts),
  );
  const expr = parts.join(" AND ");
  return parts.length > 1 ? `(${expr})` : expr;
};
